/**
 * CustomDatePicker Component
 * Displays dates in user-friendly format: "Oct 7"
 * Uses native date picker in the background for calendar selection
 */

import React, { useState, useRef } from 'react';
import { TextField, IconButton, Box, TextFieldProps } from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';

interface CustomDatePickerProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  sx?: TextFieldProps['sx'];
  disabled?: boolean;
  showYear?: boolean; // Whether to display year in the formatted date
}

/**
 * Format ISO date (YYYY-MM-DD) to display format
 * Default: "Oct 7" (no year)
 * With showYear: "Oct 7, 2025"
 */
const formatDateForDisplay = (isoDate: string, showYear: boolean = false): string => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate + 'T00:00:00'); // Add time to avoid timezone issues
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    };

    if (showYear) {
      options.year = 'numeric';
    }

    return date.toLocaleDateString('en-US', options);
  } catch {
    return '';
  }
};

/**
 * Parse display format (Oct 7) to ISO format (YYYY-MM-DD)
 * Assumes current year if no year is provided
 */
const parseDisplayToISO = (displayDate: string): string => {
  if (!displayDate) return '';
  try {
    // If display format doesn't include year, append current year
    // Check if the string contains a 4-digit year pattern
    const dateWithYear = /\d{4}/.test(displayDate) ? displayDate : `${displayDate} ${new Date().getFullYear()}`;
    const date = new Date(dateWithYear);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0] || '';
  } catch {
    return '';
  }
};

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Oct 7',
  size = 'small',
  fullWidth = true,
  variant = 'outlined',
  sx,
  disabled = false,
  showYear = false,
}) => {
  const [displayValue, setDisplayValue] = useState(formatDateForDisplay(value, showYear));
  const [isFocused, setIsFocused] = useState(false);
  const hiddenDateInputRef = useRef<HTMLInputElement>(null);

  // Update display value when prop value changes
  React.useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatDateForDisplay(value, showYear));
    }
  }, [value, isFocused, showYear]);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setDisplayValue(newValue);
  };

  const handleTextBlur = () => {
    setIsFocused(false);
    // Try to parse the entered text
    const isoDate = parseDisplayToISO(displayValue);
    if (isoDate) {
      onChange(isoDate);
      setDisplayValue(formatDateForDisplay(isoDate, showYear));
    } else if (displayValue === '') {
      onChange('');
      setDisplayValue('');
    } else {
      // Invalid date, revert to previous value
      setDisplayValue(formatDateForDisplay(value, showYear));
    }
  };

  const handleTextFocus = () => {
    setIsFocused(true);
  };

  const handleCalendarClick = () => {
    if (hiddenDateInputRef.current) {
      hiddenDateInputRef.current.showPicker?.();
    }
  };

  const handleHiddenDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(newValue);
    setDisplayValue(formatDateForDisplay(newValue, showYear));
  };

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: fullWidth ? '100%' : 'auto' }}>
      <TextField
        value={displayValue}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        onFocus={handleTextFocus}
        label={label}
        placeholder={placeholder}
        size={size}
        fullWidth={fullWidth}
        variant={variant}
        disabled={disabled}
        sx={sx}
      />

      {/* Hidden native date input for calendar picker - positioned near calendar icon */}
      <input
        ref={hiddenDateInputRef}
        type="date"
        value={value}
        onChange={handleHiddenDateChange}
        style={{
          position: 'absolute',
          right: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '32px',
          height: '32px',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }}
        disabled={disabled}
      />

      {/* Calendar icon button */}
      <IconButton
        onClick={handleCalendarClick}
        disabled={disabled}
        size="small"
        sx={{
          position: 'absolute',
          right: 8,
          color: '#6b7280',
          zIndex: 1,
          pointerEvents: disabled ? 'none' : 'auto',
          '&:hover': {
            color: '#3b82f6',
          },
        }}
      >
        <CalendarIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

export default CustomDatePicker;
